/*global MCCordovaPlugin */
import { useCallback, useMemo } from 'react';

import {
  Capacitor,
  LocalNotificationActionPerformed,
  PermissionType,
  Plugins,
  PushNotification,
  PushNotificationActionPerformed,
} from '@capacitor/core';
import { isEmpty } from 'lodash-es';
import { useNavigate } from 'react-router-dom';

import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, IMParticleCtx } from 'state/mParticle';
import { ILogEvent } from 'state/mParticle/types';
import { deviceInfo, isNative } from 'utils/environment';
import { isExternalLink } from 'utils/is-external-link';
import logger from 'utils/logger';
import {
  isNotificationsPermissionDenied,
  openPushSetting,
  selectPermission,
} from 'utils/permissions';

interface ISalesForceResult {
  handleUpdatedSalesForcePushNotification: (pushPref: string | boolean) => void;
  handleNotificationPermissionOn: (logRBIEvent: IMParticleCtx['logRBIEvent']) => void;
  addListenerPushNotificationReceived: (logRBIEvent: ILogEvent) => void;
  verifyPushSalesforce: (isMarketingPushEnabled: boolean) => void;
}

export const useSalesForcePush = (): ISalesForceResult => {
  const navigate = useNavigate();

  const { user } = useAuthContext();
  const enableSalesForcePushNotifications = useFlag(
    LaunchDarklyFlag.ENABLE_SALESFORCE_PUSH_NOTIFICATIONS
  );

  const enablePushNotificationListener = useFlag(
    LaunchDarklyFlag.ENABLE_PUSH_NOTIFICATION_LISTENER
  );

  const enableDeepLinkFromSalesforce = useFlag(LaunchDarklyFlag.ENABLE_DEEP_LINK_FROM_SALESFORCE);

  const isNativeDevice = useMemo(() => deviceInfo() !== 'web', []);

  const isPushEnable = useMemo(
    () => enableSalesForcePushNotifications && isNativeDevice && isNative && Capacitor.isNative,
    [enableSalesForcePushNotifications, isNativeDevice]
  );

  const { Device, Permissions, PushNotifications, LocalNotifications } = Plugins;

  const handleUpdatedSalesForcePushNotification = useCallback(
    (pushPref: string | boolean) => {
      if (!isPushEnable) {
        return;
      }

      const isChecked = typeof pushPref === 'string' ? pushPref === 'true' : pushPref;

      if (isChecked) {
        MCCordovaPlugin.setContactKey(
          `${user?.details.email}`,
          () => logger.info('Setting contact key'),
          error => logger.error(error)
        );
        MCCordovaPlugin.enablePush(
          () => logger.info('Push notifications enabled in SalesForce'),
          error => logger.error(error)
        );
        MCCordovaPlugin.logSdkState(
          () => logger.info('logSdkState'),
          error => logger.error(error)
        );
      } else {
        MCCordovaPlugin.disablePush(
          () => logger.info('Push notifications disabled in SalesForce'),
          error => logger.error(error)
        );
        MCCordovaPlugin.logSdkState(
          () => logger.info('logSdkState'),
          error => logger.error(error)
        );
      }
    },
    [isPushEnable, user]
  );

  const handleNotificationPermissionOn = useCallback(
    async (logRBIEvent: IMParticleCtx['logRBIEvent']) => {
      if (!isPushEnable) {
        return;
      }

      const isDenied = await isNotificationsPermissionDenied();

      if (isDenied) {
        openPushSetting();
        return;
      }

      await selectPermission({ logRBIEvent });
    },
    [isPushEnable]
  );

  const redirectDeepLink = useCallback(
    (link: string | undefined | null) => {
      if (!link) {
        return;
      }

      if (isExternalLink(link)) {
        window.location.replace(link);
        return;
      }
      navigate(link);
    },
    [navigate]
  );

  /**
   * Create a Listener to send push notification when App state is foreground
   *
   * To send push notification when App state is background or killed,
   * the Android and IOS will received it via BroadcastReceiver
   *
   * @link https://capacitorjs.com/docs/apis/push-notifications
   * @link https://firebase.google.com/docs/cloud-messaging/android/receive
   *
   * To Android version 12 or higher, this method will not used, because to use
   * this featute, the capacitor (local-notification) package should be updated to version above 4+
   *
   * @link https://github.com/ionic-team/capacitor/issues/5604
   */
  const addListenerPushNotificationReceived = useCallback(
    async (logEvent: ILogEvent) => {
      // if feature flag disable
      if (!enablePushNotificationListener) {
        return;
      }

      PushNotifications.removeAllListeners();
      LocalNotifications.removeAllListeners();

      const device = await Device.getInfo();

      // android version 12 or higher need to capacitor updated to work
      if (device.platform === 'android' && parseFloat(device.osVersion) > 11) {
        // send mparticle event
        logEvent(CustomEventNames.PUSH_NOTIFICATIONS_RESPONSE, EventTypes.Other, {
          attributes: { device, status: 'Android version above 11' },
        });
        return;
      }

      //The Push Notification API uses Firebase Cloud Messaging SDK for handling notifications
      PushNotifications.addListener(
        'pushNotificationReceived',
        async (pushNotifications: PushNotification) => {
          // verify permission
          const permissionResult = await Permissions.query({
            name: PermissionType.Notifications,
          });

          const extraData = {
            link: pushNotifications?.data?._od,
          };

          // create notification object
          const notification = {
            title: pushNotifications?.title ?? pushNotifications?.data?.title ?? '',
            body: pushNotifications?.body ?? pushNotifications?.data?.alert ?? '',
            id: new Date().getTime(),
            smallIcon: pushNotifications?.data?.icon ?? 'ic_stat_notify',
            extra: enableDeepLinkFromSalesforce ? { ...extraData } : {},
          };

          // send mparticle event
          logEvent(CustomEventNames.PUSH_NOTIFICATIONS_RESPONSE, EventTypes.Other, {
            attributes: {
              ...notification,
              state: permissionResult.state,
            },
          });

          // create local notification
          LocalNotifications.schedule({
            notifications: [notification],
          });
        }
      );

      if (enableDeepLinkFromSalesforce) {
        // Listener to set action per example deep link with app in background
        PushNotifications.addListener(
          'pushNotificationActionPerformed',
          async (notificationAction: PushNotificationActionPerformed) => {
            redirectDeepLink(notificationAction?.notification?.data?._od);
          }
        );

        // Listener to set action per example deep link with app in foreground
        LocalNotifications.addListener(
          'localNotificationActionPerformed',
          async (localNotificationAction: LocalNotificationActionPerformed) => {
            redirectDeepLink(localNotificationAction?.notification?.extra?.link);
          }
        );
      }
    },
    [
      Device,
      LocalNotifications,
      Permissions,
      PushNotifications,
      enableDeepLinkFromSalesforce,
      enablePushNotificationListener,
      redirectDeepLink,
    ]
  );

  const verifyPushSalesforce = useCallback(
    (isMarketingPushEnabled: boolean) => {
      if (!isPushEnable) {
        return;
      }

      const handlePushState = (enabled: boolean) => {
        if ((!enabled && isMarketingPushEnabled) || (enabled && !isMarketingPushEnabled)) {
          handleUpdatedSalesForcePushNotification(isMarketingPushEnabled);
        }
      };

      //Case contactKey is empty but the push is enabled in dynamo, here enable push in salesforce too
      const handleContactKey = (key: string) => {
        if (isEmpty(key) && isMarketingPushEnabled) {
          handleUpdatedSalesForcePushNotification(isMarketingPushEnabled);
        }
      };

      MCCordovaPlugin.isPushEnabled(handlePushState, error => logger.error(error));

      MCCordovaPlugin.getContactKey(handleContactKey, error => logger.error(error));
    },
    [handleUpdatedSalesForcePushNotification, isPushEnable]
  );
  return {
    handleUpdatedSalesForcePushNotification,
    handleNotificationPermissionOn,
    addListenerPushNotificationReceived,
    verifyPushSalesforce,
  };
};
